import { useInfiniteBillingEntities } from '@round/creatorbase-helpers/api-hooks';
import styles from './BillingEntitiesList.module.css';
import { getTableMetaHelper, ScrollView, Skeleton, SkeletonTableCell } from '@round/ui-kit';
import { PageTitle } from '@round/whitelabel-ui';
import { VirtualizedTable } from '@round/whitelabel-ui';
import { ColumnDef } from '@tanstack/react-table';
import { creatorbase } from '@round/api';
import useClientSelect from 'Modules/Plans/Clients/hooks/useClientSelect';
import { useFeatureFlags } from 'utility/featureFlags/FeatureFlagsContext';
import { Dropdown } from '@round/whitelabel-ui';
import { useState } from 'react';
import BillingEntitySettingsModal from '../BillingEntitySettingsModal/BillingEntitySettingsModal';

type Meta = { isLoading: boolean; onEditSettings: (billingEntity: creatorbase.BillingEntity) => void };
const getTableMeta = getTableMetaHelper<Meta>();

const PAGE_SIZE = 25;
const BillingEntitiesList = () => {
    const isBillingEntitySettingsEnabled = useFeatureFlags().isEnabled('2151_billing_entity_settings');
    const { selectedClientId } = useClientSelect();

    const columns: ColumnDef<creatorbase.BillingEntity, any>[] = [
        {
            header: 'ID',
            accessorKey: 'id',
            cell: SkeletonTableCell,
        },
        {
            header: 'Name',
            accessorKey: 'name',
            cell: SkeletonTableCell,
        },
        {
            header: 'Email',
            accessorKey: 'email_address',
            cell: SkeletonTableCell,
        },
        {
            header: 'Address',
            id: 'address',
            accessorFn: (row) => {
                const concatAddress = (address: creatorbase.Address) => {
                    let concatenatedAddress = '';
                    const fields = [
                        'attention_to',
                        'address_line1',
                        'address_line2',
                        'address_line3',
                        'address_line4',
                        'city',
                        'region',
                        'postal_code',
                        'country',
                    ];

                    fields.forEach((field) => {
                        const value = address[field as keyof creatorbase.Address];
                        if (value) {
                            if (concatenatedAddress.length > 0) {
                                concatenatedAddress += ', ';
                            }
                            concatenatedAddress += value.trim();
                        }
                    });

                    return concatenatedAddress;
                };

                const uniqueAddresses = new Set<string>();
                row.addresses.forEach((address) => {
                    const formattedAddress = concatAddress(address);
                    if (formattedAddress) {
                        uniqueAddresses.add(formattedAddress);
                    }
                });
                return Array.from(uniqueAddresses).join('\n');
            },
            meta: {
                className: styles.addressColumn,
            },
            cell: SkeletonTableCell,
        },
        {
            header: '',
            id: 'actions',
            meta: {
                className: styles.actionsColumn,
            },
            cell: ({ row: { original }, table }) => {
                const { isLoading, onEditSettings } = getTableMeta(table);

                if (isLoading) {
                    return <Skeleton />;
                }

                return (
                    <div onClick={(e) => e.stopPropagation()}>
                        <Dropdown>
                            {({ setIsOpen }) => (
                                <>
                                    <Dropdown.Target />
                                    <Dropdown.Menu>
                                        <Dropdown.Item
                                            onClick={() => {
                                                onEditSettings(original);
                                                setIsOpen(false);
                                            }}
                                        >
                                            Edit settings
                                        </Dropdown.Item>
                                    </Dropdown.Menu>
                                </>
                            )}
                        </Dropdown>
                    </div>
                );
            },
        },
    ];

    const { data, isFetching, isPending, isFetchingNextPage, status, fetchNextPage, hasNextPage } =
        useInfiniteBillingEntities(
            { client_id: selectedClientId, page_size: PAGE_SIZE },
            { refetchOnWindowFocus: false, enabled: typeof selectedClientId === 'number' }
        );

    const billingEntities = data?.pages.flatMap((page) => page.results) ?? [];
    const emptyRows = new Array(PAGE_SIZE).fill({});
    const rows = isPending || isFetchingNextPage ? billingEntities.concat(emptyRows) : billingEntities;
    const [isEditSettingsOpen, setIsEditSettingsOpen] = useState(false);
    const [billingEntityToEdit, setBillingEntityToEdit] = useState<creatorbase.BillingEntity | null>(null);

    const meta: Meta = {
        isLoading: isFetching || isPending,
        onEditSettings: (billingEntity) => {
            setBillingEntityToEdit(billingEntity);
            setIsEditSettingsOpen(true);
        },
    };

    return (
        <div className={styles.container}>
            <header className={styles.header}>
                <PageTitle>Billing Entities</PageTitle>
            </header>

            <ScrollView className={styles.main}>
                <VirtualizedTable
                    isLoading={isFetching}
                    containerClassName={styles.tableContainer}
                    meta={meta}
                    columns={columns}
                    data={rows}
                    onEndReached={() => {
                        if (status === 'success' && hasNextPage) {
                            fetchNextPage();
                        }
                    }}
                    virtualizerOptions={{
                        count: rows.length,
                        estimateSize: () => 50,
                        getItemKey: (index) => rows[index]?.id || index,
                        overscan: 5,
                    }}
                    columnVisibility={{
                        actions: isBillingEntitySettingsEnabled,
                    }}
                />
            </ScrollView>

            <BillingEntitySettingsModal
                isOpen={isEditSettingsOpen}
                onClose={() => {
                    setIsEditSettingsOpen(false);
                    setBillingEntityToEdit(null);
                }}
                billingEntity={billingEntityToEdit}
            />
        </div>
    );
};

export default BillingEntitiesList;
